import React from 'react';

import Color from 'color';

import type { TextFieldProps } from '@mui/material';
import {
  Box,
  FormHelperText,
  Grid,
  InputLabel,
  TextField,
  styled,
} from '@mui/material';
import PercentIcon from '@mui/icons-material/Percent';

import { getRecordTotal } from './ItemizedPercentileList/helpers';

import type {
  ItemizedPercentileListValue,
  ItemizedPercentileListItemValue,
} from './ItemizedPercentileList/types';
import { Show } from '../../../components/Show';

type ItemizedPercentileListProps = {
  name: string;
  listItems: { key: string; label: string; commentEligibility?: string }[];
  value: ItemizedPercentileListValue;
  onChange: (value: ItemizedPercentileListValue) => void;
  error?: string;
  disabled?: boolean;
};

export const ItemizedPercentileList = (props: ItemizedPercentileListProps) => {
  const listTotal = React.useMemo(() => {
    return getRecordTotal(props.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(props.value)]);

  const hasEvenGridItems = props.listItems.length % 2 === 0;
  const gridSize = hasEvenGridItems ? 6 : 12;

  return (
    <ListContainer container spacing={1}>
      {props.listItems.map((li) => {
        const safeProps = props.value?.props ?? {};

        const handleItemPercentFieldChange: TextFieldProps['onChange'] = (
          e
        ) => {
          const updatedListValue = {
            ...props.value,
            props: {
              ...safeProps,
              [li.key]: {
                ...safeProps[li.key],
                value: e.target.value,
              },
            },
          };

          props.onChange(updatedListValue);
        };

        const handleCommentChange: TextFieldProps['onChange'] = (e) => {
          const updatedListValue = {
            ...props.value,
            props: {
              ...safeProps,
              [li.key]: {
                ...safeProps[li.key],
                comment: e.target.value,
              },
            },
          };

          props.onChange(updatedListValue);
        };

        const itemValue = safeProps[li.key];

        if (hasEvenGridItems) {
          return (
            <TwoColumnedRow item sm={gridSize} key={li.key}>
              <ItemizedPercentileListItem
                text={li.label}
                value={itemValue}
                onPercentChange={handleItemPercentFieldChange}
                onCommentChange={handleCommentChange}
                commentEligible={Boolean(li.commentEligibility)}
                disabled={props.disabled}
              />
            </TwoColumnedRow>
          );
        }

        return (
          <SingleColumnedRow item sm={gridSize} key={li.key}>
            <ItemizedPercentileListItem
              text={li.label}
              value={itemValue}
              onPercentChange={handleItemPercentFieldChange}
              onCommentChange={handleCommentChange}
              commentEligible={Boolean(li.commentEligibility)}
              disabled={props.disabled}
            />
          </SingleColumnedRow>
        );
      })}

      <Total item sm={12}>
        <ItemizedPercentileListItem
          text="Total"
          value={{ value: String(listTotal) }}
          disabled
        />
      </Total>

      <FormHelperText
        style={{ margin: 0, marginRight: '0.5rem', textAlign: 'right' }}
      >
        {props.error}
      </FormHelperText>
    </ListContainer>
  );
};

const ItemizedPercentileListItem = (props: {
  text: string;
  value: ItemizedPercentileListItemValue;
  onPercentChange?: TextFieldProps['onChange'];
  onCommentChange?: TextFieldProps['onChange'];
  disabled?: boolean;
  commentEligible?: boolean;
}) => {
  return (
    <React.Fragment>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <InputLabel>{props.text}</InputLabel>

        <Show when={Boolean(props.commentEligible)}>
          <StyledTextField
            variant="standard"
            sx={{
              '& .MuiInputBase-input': {
                width: '8rem',
              },
            }}
            disabled={props.disabled}
            onChange={props.onCommentChange}
            value={props.value?.comment}
            placeholder="Description"
          />
        </Show>
      </Box>

      <StyledTextField
        variant="standard"
        InputProps={{ endAdornment: <PercentIcon />, inputProps: { min: 0 } }}
        type="number"
        sx={{
          '& .MuiInputBase-input': {
            width: '4rem',
          },
        }}
        value={props.value?.value}
        onChange={props.onPercentChange}
        disabled={props.disabled}
      />
    </React.Fragment>
  );
};

const RowBase = styled(Grid)`
  display: flex;
  justify-content: space-between;
  align-items: center;

  background: transparent;
  padding: 8px;
`;

const SingleColumnedRow = styled(RowBase)`
  &:nth-child(odd) {
    background: ${(props) =>
      props.theme?.palette.mode === 'light'
        ? props.theme?.palette.background.modal
        : Color(props.theme?.palette.background.modal).darken(0.15).toString()};
  }
`;

const TwoColumnedRow = styled(RowBase)`
  &:nth-child(4n + 1),
  &:nth-child(4n + 2) {
    background: ${(props) =>
      props.theme?.palette.mode === 'light'
        ? props.theme?.palette.background.modal
        : Color(props.theme?.palette.background.modal).darken(0.15).toString()};
  }
`;

const Total = styled(RowBase)`
  justify-content: flex-end;
  gap: 1rem;
`;

const StyledTextField = styled(TextField)`
  & .MuiInputBase-input {
    padding-left: 2px;
    background: transparent;
    border: none;
    border-bottom: ${(props) =>
      `1px solid ${props.theme.palette.text.contrastText}`};
    border-radius: 0;

    height: 0.15rem;

    &.Mui-disabled {
      background: transparent;
    }
  }
`;

const ListContainer = styled(Grid)`
  border-radius: 6px;
  border: 1px solid rgb(0, 0, 0, 0.1);
  margin-top: 2px;
`;
